import { AlertTriangle, CheckCircle2, Route } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";

import apiClient from "../../lib/api/client";
import { extractErrorMessage } from "../../lib/api";

export const MileageSection = () => {
  const [status, setStatus] = useState(null);
  const [checking, setChecking] = useState(false);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  const checkStatus = useCallback(async () => {
    try {
      setChecking(true);
      const { data } = await apiClient.get("/mileage/status");
      setStatus(data);
    } catch (error) {
      if (error?.response?.status !== 403) {
        toast.error(extractErrorMessage(error, "Unable to check the mileage service."));
      }
      setStatus(null);
    } finally {
      setChecking(false);
    }
  }, []);

  useEffect(() => { checkStatus(); }, [checkStatus]);

  const recalculate = async () => {
    try {
      setRunning(true);
      const { data } = await apiClient.post("/mileage/recalculate-missing", null, { params: { limit: 40 } });
      setResult(data);
      if (data.ok) {
        toast.success(`Miles recalculated for ${data.updated} load(s)${data.remaining ? ` — ${data.remaining} still waiting, run again` : ""}.`);
      } else {
        toast.error(data.error || "Google Routes is still unavailable.");
      }
      await checkStatus();
    } catch (error) {
      toast.error(extractErrorMessage(error, "Unable to recalculate miles."));
    } finally {
      setRunning(false);
    }
  };

  if (!status) {
    return null;
  }

  return (
    <section className="panel-surface px-6 py-6" data-testid="settings-mileage-section">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-zinc-500">Mileage</p>
          <h2 className="mt-2 flex items-center gap-2 text-3xl font-semibold text-zinc-900"><Route className="h-6 w-6 text-zinc-400" />Google Routes</h2>
          <p className="mt-2 max-w-2xl text-sm text-zinc-600">Load and trip miles are looked up from Google when a load is saved. If Google rejects the key, loads are saved with blank miles until the key is fixed.</p>
        </div>
        <div className={`rounded-2xl border px-4 py-3 ${status.ok ? "border-emerald-200 bg-emerald-50" : "border-amber-200 bg-amber-50"}`} data-testid="mileage-status-card">
          <p className={`inline-flex items-center gap-2 text-sm font-semibold ${status.ok ? "text-emerald-800" : "text-amber-900"}`} data-testid="mileage-status-label">
            {status.ok ? <CheckCircle2 className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}
            {status.ok ? `Working — test route ${status.test_miles} mi` : "Not working"}
          </p>
          {!status.ok ? <p className="mt-1 max-w-sm text-xs text-amber-900" data-testid="mileage-status-error">{status.error}</p> : null}
          <p className="mt-2 text-xs text-zinc-600" data-testid="mileage-missing-count">{status.missing_active_count} active load(s) without miles</p>
        </div>
      </div>

      {!status.ok && status.hint ? <p className="mt-4 rounded-2xl border border-dashed border-amber-300 px-4 py-3 text-sm text-zinc-700" data-testid="mileage-status-hint">{status.hint}</p> : null}

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <button className="rounded-2xl border border-zinc-200 px-4 py-3 text-sm font-semibold text-zinc-700 transition hover:border-zinc-900 hover:text-zinc-900 disabled:opacity-60" data-testid="mileage-recheck-button" disabled={checking} onClick={checkStatus} type="button">{checking ? "Checking…" : "Re-check Google"}</button>
        <button className="rounded-2xl bg-zinc-900 px-5 py-3 text-sm font-semibold text-white transition hover:bg-zinc-800 disabled:opacity-60" data-testid="mileage-recalculate-button" disabled={running || !status.ok || !status.missing_active_count} onClick={recalculate} type="button">{running ? "Recalculating…" : "Recalculate missing miles"}</button>
      </div>
      {result?.ok ? (
        <p className="mt-3 text-xs text-zinc-600" data-testid="mileage-recalculate-result">Updated {result.updated}, failed {result.failed}, remaining {result.remaining}.{result.failures?.length ? ` First issue: ${result.failures[0].load_number} — ${result.failures[0].reason}` : ""}</p>
      ) : null}
    </section>
  );
};
